import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Modal,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { hasSameContents } from '../../../../../utils/arrayUtils.js';
import axios from 'axios';
import { skillingSheetDropdownOptions } from '../skillingSheetUtils.js';
import { appInsights } from '../../../../../telemetry/appInsights.js';

export default function EditSkillingSheet({
  isOpen,
  handleClose,
  entry,
  handleRefresh,
  setIsSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
  user
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState({ ...entry });
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const trackSkillRemoval = data => appInsights.trackEvent({ name: 'SkillRemoval' }, data);
  const trackSkillUpdate = data => appInsights.trackEvent({ name: 'SkillUpdate' }, data);

  const hideDialog = () => {
    setIsDialogOpen(false);
  };

  const hasChanges = () => {
    return !hasSameContents(Object.values(formState), Object.values(entry));
  };

  const calculateChange = () => {
    const changes = [];
    Object.keys(formState).forEach(key => {
      if (formState[key] !== entry[key]) {
        changes.push({ [key]: formState[key] === '1' ? 1 : formState[key] });
      }
    });
    return changes;
  };

  const handleReset = () => {
    setFormState({ ...entry });
  };

  const removeSkills = () => {
    setIsDialogOpen(true);
  };

  async function removeSkillsConfirmed() {
    try {
      setIsSubmitting(true);
      const response = await axios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/remove`,
        data: { userId: formState.userId, deleteSelected }, // only send the changes instead of the entire entry
      });
      trackSkillRemoval({ skillRemovedBy: user, skillRemovedFor: formState.name, removedSkills: deleteSelected, response, sheetType: 'Specialty' });
      setSnackbarMessage('Skills removed successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error removing skills:', error);
      setSnackbarMessage(
        'There was an error removing the skills. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  async function submit() {
    try {
      setIsSubmitting(true);
      const calculatedChanges = calculateChange();
      const response = await axios({
        method: 'PATCH',
        url: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/update`,
        data: { userId: formState.userId, changes: calculatedChanges }, // only send the changes instead of the entire entry
      });
      trackSkillUpdate({ skillUpdatedBy: user, skillUpdatedFor: formState.name, updatedSkills: calculatedChanges, response, sheetType: 'Specialty' });
      setSnackbarMessage('Specialty skilling sheet updated successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error submitting specialty skilling sheet edit:', error);
      setSnackbarMessage(
        'There was an error submitting the specialty skilling sheet update. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Grid
          container
          spacing={2}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '90vw', md: 800 },
            bgcolor: 'var(--dust-white)',
            p: 4,
            overflowY: 'auto',
            maxHeight: { xs: '90%', md: '90%' },
          }}
        >
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography variant='h6' component='h2'>
              Edit Skilling Sheet - {entry.name}
            </Typography>
            <IconButton
              sx={{
                color: 'black',
                '&:hover': {
                  color: 'var(--arcturus-red)',
                },
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.stolenReports}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'stolenReports']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'stolenReports'),
                    );
                  }
                }}
                checked={deleteSelected.includes('stolenReports')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Stolen Reports'}</InputLabel>
                <Select
                  label={'Stolen Reports'}
                  value={formState.stolenReports || ''}
                  onChange={e =>
                    setFormState({
                      ...formState,
                      stolenReports: e.target.value,
                    })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.stolenTickets}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'stolenTickets']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'stolenTickets'),
                    );
                  }
                }}
                checked={deleteSelected.includes('stolenTickets')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Stolen Tickets'}</InputLabel>
                <Select
                  label={'Stolen Tickets'}
                  value={formState.stolenTickets || ''}
                  onChange={e =>
                    setFormState({
                      ...formState,
                      stolenTickets: e.target.value,
                    })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.txStolen}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'txStolen']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'txStolen'),
                    );
                  }
                }}
                checked={deleteSelected.includes('txStolen')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Texas Stolen'}</InputLabel>
                <Select
                  label={'Texas Stolen'}
                  value={formState.txStolen || ''}
                  onChange={e =>
                    setFormState({ ...formState, txStolen: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.imo}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'imo']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'imo'),
                    );
                  }
                }}
                checked={deleteSelected.includes('imo')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'IMO'}</InputLabel>
                <Select
                  label={'IMO'}
                  value={formState.imo || ''}
                  onChange={e =>
                    setFormState({ ...formState, imo: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.nrcHelpdesk}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'nrcHelpdesk']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'nrcHelpdesk'),
                    );
                  }
                }}
                checked={deleteSelected.includes('nrcHelpdesk')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'NRC Helpdesk'}</InputLabel>
                <Select
                  label={'NRC Helpdesk'}
                  value={formState.nrcHelpdesk || ''}
                  onChange={e =>
                    setFormState({ ...formState, nrcHelpdesk: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.sap}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'sap']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'sap'),
                    );
                  }
                }}
                checked={deleteSelected.includes('sap')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'SAP'}</InputLabel>
                <Select
                  label={'SAP'}
                  value={formState.sap || ''}
                  onChange={e =>
                    setFormState({ ...formState, sap: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.fwdToSite}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'fwdToSite']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'fwdToSite'),
                    );
                  }
                }}
                checked={deleteSelected.includes('fwdToSite')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Forward to Site'}</InputLabel>
                <Select
                  label={'Forward to Site'}
                  value={formState.fwdToSite || ''}
                  onChange={e =>
                    setFormState({ ...formState, fwdToSite: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.supCalls}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'supCalls']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'supCalls'),
                    );
                  }
                }}
                checked={deleteSelected.includes('supCalls')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Supervisor Calls'}</InputLabel>
                <Select
                  label={'Supervisor Calls'}
                  value={formState.supCalls || ''}
                  onChange={e =>
                    setFormState({ ...formState, supCalls: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.imoTrainer}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'imoTrainer']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'imoTrainer'),
                    );
                  }
                }}
                checked={deleteSelected.includes('imoTrainer')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'IMO Trainer'}</InputLabel>
                <Select
                  label={'IMO Trainer'}
                  value={formState.imoTrainer || ''}
                  onChange={e =>
                    setFormState({ ...formState, imoTrainer: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.dispatchEscalations}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([
                      ...deleteSelected,
                      'dispatchEscalations',
                    ]);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(
                        item => item !== 'dispatchEscalations',
                      ),
                    );
                  }
                }}
                checked={deleteSelected.includes('dispatchEscalations')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Dispatch Escalations'}</InputLabel>
                <Select
                  label={'Dispatch Escalations'}
                  value={formState.dispatchEscalations || ''}
                  onChange={e =>
                    setFormState({
                      ...formState,
                      dispatchEscalations: e.target.value,
                    })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.workforce}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'workforce']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'workforce'),
                    );
                  }
                }}
                checked={deleteSelected.includes('workforce')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Workforce Backup'}</InputLabel>
                <Select
                  label={'Workforce Backup'}
                  value={formState.workforce || ''}
                  onChange={e =>
                    setFormState({ ...formState, workforce: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.dsrLine}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'dsrLine']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'dsrLine'),
                    );
                  }
                }}
                checked={deleteSelected.includes('dsrLine')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'DSR Line'}</InputLabel>
                <Select
                  label={'DSR Line'}
                  value={formState.dsrLine || ''}
                  onChange={e =>
                    setFormState({ ...formState, dsrLine: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.tier2}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'tier2']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'tier2'),
                    );
                  }
                }}
                checked={deleteSelected.includes('tier2')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Tier 2'}</InputLabel>
                <Select
                  label={'Tier 2'}
                  value={formState.tier2 || ''}
                  onChange={e =>
                    setFormState({ ...formState, tier2: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.flLiveAlerts}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'flLiveAlerts']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'flLiveAlerts'),
                    );
                  }
                }}
                checked={deleteSelected.includes('flLiveAlerts')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Florida Live Alerts'}</InputLabel>
                <Select
                  label={'Florida Live Alerts'}
                  value={formState.flLiveAlerts || ''}
                  onChange={e =>
                    setFormState({ ...formState, flLiveAlerts: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={
                  <DeleteIcon sx={{ color: 'var(--arcturus-red)' }} />
                }
                sx={{ marginBottom: '20px' }}
                disabled={!entry.blitzz}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'blitzz']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'blitzz'),
                    );
                  }
                }}
                checked={deleteSelected.includes('blitzz')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Blitzz'}</InputLabel>
                <Select
                  label={'Blitzz'}
                  value={formState.blitzz || ''}
                  onChange={e =>
                    setFormState({ ...formState, blitzz: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Tooltip
              title={'This will reset all fields to inital values.'}
              arrow
              placement='top'
            >
              <span>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: 'var(--aldebaran-yellow)',
                    color: 'black',
                  }}
                  disabled={!hasChanges() || isSubmitting}
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title='This will remove any skill selection from selected fields.'
              arrow
              placement='top'
            >
              <span>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: 'var(--arcturus-red)',
                  }}
                  onClick={removeSkills}
                  disabled={!deleteSelected.length || isSubmitting}
                >
                  Remove Skills
                </Button>
              </span>
            </Tooltip>
            <Button
              disabled={!hasChanges() || isSubmitting}
              variant='contained'
              sx={{ backgroundColor: 'var(--vega-blue)' }}
              onClick={submit}
            >
              {'Submit'}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <Dialog open={isDialogOpen} onClose={hideDialog}>
        <DialogTitle>Are you sure you want to remove skills?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will reset the selection for the selected skills
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            onClick={hideDialog}
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: 'black',
            }}
          >
            No
          </Button>
          <Button
            onClick={removeSkillsConfirmed}
            variant='contained'
            sx={{
              backgroundColor: 'var(--arcturus-red)',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
